import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import { X } from "lucide-react";
import { Button } from "components/common/Button";
import { InlineSwitcher } from "components/common/InlineSwitcher";
import { HistoryList } from "./HistoryList";
import { historySubjectIdsOf } from "./utils/historySubjectIdsOf";
import type { InlineSwitcherOption } from "components/common/InlineSwitcher/types";
import type { HistoryDrawerProps, HistoryScope } from "./types";

export const HistoryDrawer = ({
  projectId,
  subject,
  isOpen,
  onClose,
  revertingEntryId,
  onRevert,
}: HistoryDrawerProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scope, setScope] = useState<HistoryScope>("subject");
  // With nothing selected only the project scope exists; the chosen scope returns with the next selection.
  const effectiveScope: HistoryScope = subject === null ? "project" : scope;
  const scopedSubject = effectiveScope === "subject" ? subject : null;
  const scopedIds = historySubjectIdsOf(scopedSubject);

  if (!isOpen) return null;

  const scopeOptions: InlineSwitcherOption<HistoryScope>[] = [
    {
      value: "subject",
      label: t(
        subject?.kind === "diagram"
          ? "smartSurveyor.history.scopeDiagram"
          : "smartSurveyor.history.scopePlan",
      ),
      disabled: subject === null,
    },
    { value: "project", label: t("smartSurveyor.history.scopeProject") },
  ];

  return (
    <aside className="flex h-full w-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-2">
        <h2 className="text-sm font-semibold text-gray-800">
          {t("smartSurveyor.history.title")}
        </h2>
        <InlineSwitcher
          variant="segmented"
          value={effectiveScope}
          options={scopeOptions}
          onChange={setScope}
          className="ml-auto w-60"
          itemClassName="text-xs"
        />
        <Button
          variant="text"
          size="sm"
          onClick={onClose}
          aria-label={t("smartSurveyor.common.close")}
        >
          <X size={18} />
        </Button>
      </header>
      {/* A new plan, diagram or scope remounts the list, which starts again at the newest page. */}
      <HistoryList
        key={`${scopedIds.floorPlanId}:${scopedIds.diagramId}`}
        projectId={projectId}
        subject={scopedSubject}
        currentUserEmail={user?.email ?? null}
        revertingEntryId={revertingEntryId}
        onRevert={onRevert}
      />
    </aside>
  );
};
